import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { batchAPI } from '../../utils/api';
import { Spinner } from '../../components/ui';
import toast from 'react-hot-toast';

const OPT_COLORS = {
  correct:  { bg:'#e8f5e9', border:'#28A745', color:'#28A745' },
  wrong:    { bg:'#fdecea', border:'#dc3545', color:'#dc3545' },
  neutral:  { bg:'var(--bg)', border:'var(--border)', color:'var(--text)' },
};

export default function TestResult() {
  const { batchId, resultId } = useParams();
  const location = useLocation();
  const nav = useNavigate();

  const [result,  setResult]  = useState(location.state?.result  || null);
  const [answers, setAnswers] = useState(location.state?.answers || []);
  const [loading, setLoading] = useState(!location.state?.result);
  const [showReview, setShowReview] = useState(false);
  const [filterReview, setFilterReview] = useState('all');

  useEffect(() => {
    if (location.state?.result) return;
    batchAPI.getResult(batchId, resultId)
      .then(r => { setResult(r.data.result); setAnswers(r.data.answers); })
      .catch(() => toast.error('Failed to load result'))
      .finally(() => setLoading(false));
  }, [batchId, resultId]);

  if (loading) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg)', flexDirection:'column', gap:16 }}>
      <Spinner size={40} color="blue" />
      <p style={{ color:'var(--muted)' }}>Loading result...</p>
    </div>
  );

  if (!result) return null;

  const pct = parseFloat(result.percentage) || 0;
  const grade =
    pct >= 80 ? { label:'Excellent! 🌟', color:'#28A745' } :
    pct >= 60 ? { label:'Good Job! 👍',  color:'var(--blue)' } :
    pct >= 40 ? { label:'Keep Going 💪', color:'#F5A623' } :
                { label:'Try Again 📚',  color:'#dc3545' };

  const filteredAnswers = answers.filter(a => {
    if (filterReview === 'all')     return true;
    if (filterReview === 'correct') return a.is_correct;
    if (filterReview === 'wrong')   return !a.is_correct && a.selected_answer !== 'skipped';
    if (filterReview === 'skipped') return a.selected_answer === 'skipped';
    return true;
  });

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', padding:'20px 16px' }}>
      <div style={{ maxWidth:750, margin:'0 auto' }}>

        {/* ── Score Card ── */}
        <div style={{
          background:'linear-gradient(135deg,var(--blue) 0%,var(--blue-mid) 100%)',
          borderRadius:20, padding:'32px 28px', marginBottom:20, color:'#fff',
        }}>
          {/* Header */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 }}>
            <div>
              <div style={{ fontSize:13, opacity:0.8, marginBottom:4 }}>
                📝 Batch {result.batch_number || result.batchNumber}
                {(result.batch_number === 100 || result.batchNumber === 100) && ' 🏆'}
              </div>
              <div style={{ fontFamily:'var(--font-heading)', fontWeight:900, fontSize:22 }}>
                {result.batch_title || result.batchTitle || 'Mock Test Result'}
              </div>
            </div>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontFamily:'var(--font-heading)', fontWeight:900, fontSize:52, lineHeight:1 }}>
                {pct.toFixed(1)}%
              </div>
              <div style={{ fontSize:16, fontWeight:700, marginTop:4 }}>{grade.label}</div>
            </div>
          </div>

          {/* Score breakdown */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
            {[
              { label:'Correct',  value: result.correct,  icon:'✅', color:'#a8f0c6' },
              { label:'Wrong',    value: result.wrong,    icon:'❌', color:'#ffb3b3' },
              { label:'Skipped',  value: result.skipped,  icon:'⏭',  color:'#ffe0a3' },
              { label:'Time',     value: `${result.time_taken_minutes || result.timeTaken || 0}m`, icon:'⏱', color:'#b3d4ff' },
            ].map(s => (
              <div key={s.label} style={{
                background:'rgba(255,255,255,0.15)', borderRadius:12, padding:'12px 8px', textAlign:'center',
              }}>
                <div style={{ fontSize:22, marginBottom:4 }}>{s.icon}</div>
                <div style={{ fontFamily:'var(--font-heading)', fontWeight:900, fontSize:22 }}>{s.value}</div>
                <div style={{ fontSize:11, opacity:0.8 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Progress ring visual */}
          <div style={{ marginTop:20, background:'rgba(255,255,255,0.12)', borderRadius:12, height:10, overflow:'hidden' }}>
            <div style={{ height:'100%', background:'#fff', borderRadius:99, width:`${pct}%`, transition:'width 0.8s ease' }} />
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:6, fontSize:11, opacity:0.7 }}>
            <span>0%</span><span>50%</span><span>100%</span>
          </div>
        </div>

        {/* ── Next Batch unlock banner ── */}
        {result.nextBatchNumber && (
          <div style={{
            background:'linear-gradient(135deg,#e8f5e9,#d4edda)', border:'2px solid #28A745',
            borderRadius:16, padding:'16px 20px', marginBottom:20,
            display:'flex', alignItems:'center', justifyContent:'space-between', gap:12,
          }}>
            <div>
              <div style={{ fontFamily:'var(--font-heading)', fontWeight:800, fontSize:16, color:'#28A745', marginBottom:3 }}>
                🔓 Batch {result.nextBatchNumber} Unlocked!
              </div>
              <div style={{ fontSize:13, color:'#155724' }}>
                Great work! Your next mock test is ready.
              </div>
            </div>
            <button className="btn btn-primary"
              style={{ background:'#28A745', borderColor:'#28A745', whiteSpace:'nowrap' }}
              onClick={() => nav('/dashboard/tests')}>
              Continue →
            </button>
          </div>
        )}

        {result.isLastBatch && (
          <div style={{
            background:'linear-gradient(135deg,#FFF9E6,#FFF3CD)', border:'2px solid #FFD700',
            borderRadius:16, padding:'20px', marginBottom:20, textAlign:'center',
          }}>
            <div style={{ fontSize:52, marginBottom:8 }}>🏆</div>
            <div style={{ fontFamily:'var(--font-heading)', fontWeight:900, fontSize:22, color:'#856404' }}>
              Congratulations! You completed all 100 Batches!
            </div>
            <div style={{ color:'#856404', fontSize:14, marginTop:6 }}>
              You are a TNPSC Champion! 🎉
            </div>
          </div>
        )}

        {/* ── Actions ── */}
        <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap' }}>
          <button className="btn btn-outline" onClick={() => nav('/dashboard/tests')}>
            ← My Batches
          </button>
          <button className="btn btn-outline" onClick={() => nav('/dashboard/history')}>
            📊 History
          </button>
          <button className="btn btn-primary" onClick={() => setShowReview(!showReview)}>
            {showReview ? '✕ Hide Review' : '📖 Review Answers'}
          </button>
          <button className="btn btn-outline" onClick={() => nav(`/dashboard/batch/${batchId}/test`)}>
            🔁 Retry Batch
          </button>
        </div>

        {/* ── Answer Review ── */}
        {showReview && (
          <div>
            {/* Filter tabs */}
            <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
              {[
                { key:'all',     label:`All (${answers.length})` },
                { key:'correct', label:`✅ Correct (${answers.filter(a=>a.is_correct).length})` },
                { key:'wrong',   label:`❌ Wrong (${answers.filter(a=>!a.is_correct && a.selected_answer!=='skipped').length})` },
                { key:'skipped', label:`⏭ Skipped (${answers.filter(a=>a.selected_answer==='skipped').length})` },
              ].map(f => (
                <button key={f.key} onClick={() => setFilterReview(f.key)}
                  style={{
                    padding:'7px 14px', borderRadius:99, fontSize:12, fontWeight:600,
                    border:'none', cursor:'pointer',
                    background: filterReview===f.key ? 'var(--blue)' : 'var(--border)',
                    color: filterReview===f.key ? '#fff' : 'var(--text)',
                  }}>
                  {f.label}
                </button>
              ))}
            </div>

            {/* Questions */}
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              {filteredAnswers.map((a, idx) => {
                const isSkipped = a.selected_answer === 'skipped';
                return (
                  <div key={a.question_id || idx} style={{
                    background:'#fff', borderRadius:14, border:'1px solid var(--border)',
                    padding:'18px 20px', borderLeft:`4px solid ${a.is_correct ? '#28A745' : isSkipped ? '#F5A623' : '#dc3545'}`
                  }}>
                    <div style={{ display:'flex', gap:10, alignItems:'flex-start', marginBottom:14 }}>
                      <span style={{
                        background: a.is_correct ? '#e8f5e9' : isSkipped ? '#fff3cd' : '#fdecea',
                        color: a.is_correct ? '#28A745' : isSkipped ? '#856404' : '#dc3545',
                        fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:99, flexShrink:0,
                      }}>
                        {a.is_correct ? '✅ Correct' : isSkipped ? '⏭ Skipped' : '❌ Wrong'}
                      </span>
                      <p style={{ margin:0, fontSize:14, lineHeight:1.7, fontWeight:500, color:'var(--text)' }}>
                        {a.question_text}
                      </p>
                    </div>

                    {/* Options */}
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:10 }}>
                      {['A','B','C','D'].map(opt => {
                        const optText = a[`option_${opt.toLowerCase()}`];
                        const isCorrect = opt === a.correct_answer;
                        const isSelected = opt === a.selected_answer;
                        const style = isCorrect ? OPT_COLORS.correct :
                                      (isSelected && !isCorrect) ? OPT_COLORS.wrong :
                                      OPT_COLORS.neutral;
                        return (
                          <div key={opt} style={{
                            padding:'8px 12px', borderRadius:10,
                            background: style.bg, border:`1.5px solid ${style.border}`,
                            fontSize:13, display:'flex', gap:8, alignItems:'center',
                          }}>
                            <span style={{ fontWeight:700, color:style.color, flexShrink:0 }}>{opt}.</span>
                            <span style={{ color: style.color }}>{optText}</span>
                            {isCorrect && <span style={{ marginLeft:'auto', fontSize:14 }}>✓</span>}
                            {isSelected && !isCorrect && <span style={{ marginLeft:'auto', fontSize:14 }}>✗</span>}
                          </div>
                        );
                      })}
                    </div>

                    {/* Your answer */}
                    <div style={{ fontSize:12, color:'var(--muted)', display:'flex', gap:16 }}>
                      <span>Your answer: <strong style={{ color: a.is_correct ? '#28A745' : '#dc3545' }}>
                        {isSkipped ? 'Not attempted' : a.selected_answer}
                      </strong></span>
                      <span>Correct: <strong style={{ color:'#28A745' }}>{a.correct_answer}</strong></span>
                    </div>

                    {/* Explanation */}
                    {a.explanation && (
                      <div style={{
                        marginTop:10, padding:'10px 14px',
                        background:'linear-gradient(135deg,#f0f4ff,#e8edf9)',
                        borderRadius:10, fontSize:13, color:'var(--text)', lineHeight:1.6,
                      }}>
                        <span style={{ fontWeight:700, color:'var(--blue)' }}>💡 </span>
                        {a.explanation}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
